import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

interface TabsProps {
  tabs: string[];
  defaultActiveTab?: number;
  onTabChange?: (index: number) => void;
  className?: string;
  currentTabIdx?: number;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs = ['General', 'Sources'],
  defaultActiveTab = 0,
  onTabChange,
  className = '',
  currentTabIdx,
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateUnderline = () => {
      if (tabsRef.current) {
        // Access the flex container first, then get the active tab
        const tabsContainer = tabsRef.current.querySelector('.flex');
        if (tabsContainer && activeTab < tabs.length) {
          const activeTabElement = tabsContainer.children[activeTab] as HTMLElement;
          setUnderlineStyle({
            left: activeTabElement.offsetLeft,
            width: activeTabElement.offsetWidth,
          });
        }
      }
    };

    // Initial calculation
    updateUnderline();

    // Add window resize listener to handle responsive changes
    window.addEventListener('resize', updateUnderline);
    return () => window.removeEventListener('resize', updateUnderline);
  }, [activeTab, tabs.length]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  useEffect(() => {
    if (currentTabIdx !== undefined && currentTabIdx !== activeTab) {
      handleTabClick(currentTabIdx);
    }
  });

  return (
    <div className={classNames('relative border-b border-gray-300', className)} ref={tabsRef}>
      <div className="flex">
        {tabs.map((tab, index) => (
          <div
            key={index}
            className={`px-4 py-2 cursor-pointer ${index === activeTab ? 'font-medium text-black' : 'text-gray-500'}`}
            onClick={() => handleTabClick(index)}
          >
            {tab}
          </div>
        ))}
      </div>
      <div
        className="absolute bottom-0 h-0.5 bg-black transition-all duration-300"
        style={{ left: underlineStyle.left, width: underlineStyle.width }}
      />
    </div>
  );
};
